import I18n from 'i18n-js';
import React, { useState } from 'react';

import { RCAlertBox, RCModal } from '@reverbdotcom/cadence/components';
import { core_apimessages_ShippingLabelPackage } from '@reverbdotcom/commons/src/gql/graphql';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';

import { packageEntryComplete } from '../../../../discovery/shipping_labels/shipping_label_package';
import { carrierCalculatedPackageValidator, oversizePackageWarningKey } from './carrierCalculatedPackageValidator';
import { UPS_LONGEST_SIDE_INCHES, UPS_HEAVY_PACKAGE_STICKER_REQUIRED } from './carrierCalculatedUpsPackageValidator';
import PackageInput from '../../../../discovery/shipping_labels/package_input';
import { PackageAttribute } from '../../../../discovery/shipping_labels/custom_package_inputs';

function PackageAlert({ error, warningKey }: { error: string, warningKey: string }) {
  if (!error && !warningKey) { return null; }

  let alertType;
  if (error) {
    alertType = 'error';
  } else if (warningKey === 'approachingOversized') {
    alertType = 'warning';
  } else if (warningKey === 'exceedsOversized') {
    alertType = 'info';
  }

  return (
    <div className="mt-4" data-testid="package-warning">
      <RCAlertBox type={alertType} >
        {error || I18n.t(`discovery.sellForm.shippingSection.carrierCalculatedForm.packageWarnings.${warningKey}`)}
      </RCAlertBox>
    </div>
  );
}

interface IProps {
  packageEntry?: core_apimessages_ShippingLabelPackage;
  setPackageEntry: (core_apimessages_ShippingLabelPackage) => void;
  setIsModalOpen: (boolean) => void;
}

export default function CarrierCalculatedForm({
  packageEntry,
  setPackageEntry,
  setIsModalOpen,
}: IProps) {
  const [wipPackageState, setWipPackageState] = useState({ ...packageEntry });
  const carrierCalculatedPackageValidatorResponse = carrierCalculatedPackageValidator(wipPackageState);
  const maxLengthInches = String(UPS_LONGEST_SIDE_INCHES);
  const maxWeightPounds = String(UPS_HEAVY_PACKAGE_STICKER_REQUIRED);
  const maxCubicInches = String(null);
  const disabled = !!carrierCalculatedPackageValidatorResponse?.errorTranslationKey || !packageEntryComplete(wipPackageState);
  const warningKey = oversizePackageWarningKey(wipPackageState);
  const error = carrierCalculatedPackageValidatorResponse?.errorTranslationKey ?
    I18n.t(carrierCalculatedPackageValidatorResponse?.errorTranslationKey, {
      maxLengthInches,
      maxWeightPounds,
      maxCubicInches,
    }) :
    null;

  return (
    <RCModal
      title={I18n.t('discovery.sellForm.shippingSection.carrierCalculatedForm.modalHeader')}
      actions={{
        primary: {
          onClick: () => !disabled && setPackageEntry({ ... wipPackageState }),
          preventCloseOnClick: disabled,
          isSubmit: true,
          form: 'wip-package',
        },
        secondary: true,
      }}
      onOpenChange={(state) => setIsModalOpen(state)}
      isOpen
    >
      <div className="mb-4">
        <RCAlertBox
          type="plain"
          small
        >
          <span>
            {I18n.t('discovery.sellForm.shippingSection.carrierCalculatedForm.accuracyWarningV2')}
          </span>
          <CoreLink
            className="ml-1 text-link weight-bold"
            to="https://help.reverb.com/hc/articles/360013150694-Why-Did-I-Receive-a-Shipping-Label-Adjustment-#Avoid"
            target="_blank"
          >
            {I18n.t('discovery.sellForm.shippingSection.carrierCalculatedForm.accuracyWarningLinkV2')}
          </CoreLink>
        </RCAlertBox>
      </div>

      <div className="d-flex fx-dir-col gap-4">
        <form id="wip-package">
          <div className="mb-4">
            <PackageInput
              dimensionKey={PackageAttribute.LENGTH}
              value={wipPackageState.length.value}
              shippingPackage={wipPackageState}
              measurementUnit={wipPackageState.length.unit}
              onUpdate={setWipPackageState}
              roundUpUserInput
            />
          </div>
          <div className="mb-4">
            <PackageInput
              dimensionKey={PackageAttribute.WIDTH}
              value={wipPackageState.width.value}
              shippingPackage={wipPackageState}
              measurementUnit={wipPackageState.width.unit}
              onUpdate={setWipPackageState}
              roundUpUserInput
            />
          </div>
          <div className="mb-4">
            <PackageInput
              dimensionKey={PackageAttribute.HEIGHT}
              value={wipPackageState.height.value}
              shippingPackage={wipPackageState}
              measurementUnit={wipPackageState.height.unit}
              onUpdate={setWipPackageState}
              roundUpUserInput
            />
          </div>
          <PackageInput
            dimensionKey={PackageAttribute.WEIGHT}
            value={wipPackageState.weight.value}
            shippingPackage={wipPackageState}
            measurementUnit={wipPackageState.weight.unit}
            onUpdate={setWipPackageState}
          />
        </form>
      </div>

      <PackageAlert error={error} warningKey={warningKey} />
    </RCModal>
  );
}
